import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';

const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const SCHEDULES = ['MANUAL', 'DAILY', 'HOURLY'];

const SEV_COLOR = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#d97706', LOW: '#2563eb' };

export default function MonitoringTab({ orgId }) {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [form, setForm] = useState(null);

  const loadConfig = useCallback(() => {
    const params = orgId ? { orgId } : {};
    axios.get('/iasm/alert-config', { params }).then(r => {
      setConfig(r.data);
      setForm({ ...r.data });
    }).catch(() => {});
  }, [orgId]);

  const loadLogs = useCallback(() => {
    const params = { page: 0, size: 20 };
    if (orgId) params.orgId = orgId;
    axios.get('/iasm/alert-logs', { params }).then(r => {
      setLogs(r.data.content);
      setTotalLogs(r.data.totalElements);
    }).catch(() => {});
  }, [orgId]);

  useEffect(() => { loadConfig(); loadLogs(); }, [loadConfig, loadLogs]);

  const saveConfig = () => {
    setSaving(true);
    const params = orgId ? { orgId } : {};
    axios.put('/iasm/alert-config', form, { params })
      .then(r => { setConfig(r.data); setForm({ ...r.data }); })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const testEmail = () => {
    setTesting(true);
    setTestResult(null);
    const params = orgId ? { orgId } : {};
    axios.post('/iasm/alert-config/test', null, { params })
      .then(r => setTestResult(r.data))
      .catch(e => setTestResult({ error: e.response?.data?.error || t('MonitoringTab.testFailed') }))
      .finally(() => setTesting(false));
  };

  const runAudit = () => {
    setRunning(true);
    setRunResult(null);
    const params = orgId ? { orgId } : {};
    axios.post('/iasm/audit/run-and-log', null, { params })
      .then(r => { setRunResult(r.data); loadLogs(); })
      .catch(() => setRunResult({ errorMessage: t('MonitoringTab.auditFailed') }))
      .finally(() => setRunning(false));
  };

  if (!form) return <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;

  const dirty = JSON.stringify(form) !== JSON.stringify(config);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: '24px', alignItems: 'start' }}>

      {/* Config card */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
            <i className="fas fa-bell" style={{ marginRight: '8px', color: '#7c3aed' }} />{t('MonitoringTab.alertSettings')}
          </h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
            <Toggle checked={form.enabled} onChange={v => setForm(p => ({ ...p, enabled: v }))} />
            {form.enabled ? t('MonitoringTab.enabled') : t('MonitoringTab.disabled')}
          </label>
        </div>

        <Field label={t('MonitoringTab.recipientEmail')}>
          <input
            value={form.recipientEmail || ''}
            onChange={e => setForm(p => ({ ...p, recipientEmail: e.target.value }))}
            placeholder="security@yourcompany.com"
            style={inputStyle}
          />
        </Field>

        <Field label={t('MonitoringTab.alertThreshold')}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {SEVERITIES.map(s => (
              <button key={s} onClick={() => setForm(p => ({ ...p, minSeverity: s }))}
                style={{
                  padding: '5px 10px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                  fontSize: '11px', fontWeight: 600,
                  background: form.minSeverity === s ? SEV_COLOR[s] : '#f3f4f6',
                  color: form.minSeverity === s ? '#fff' : '#374151',
                }}>{s}</button>
            ))}
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
            {t('MonitoringTab.alertThresholdHelp')}
          </div>
        </Field>

        <Field label={t('MonitoringTab.scanSchedule')}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {SCHEDULES.map(s => (
              <button key={s} onClick={() => setForm(p => ({ ...p, scheduleType: s }))}
                style={{
                  padding: '5px 14px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                  fontSize: '12px', fontWeight: form.scheduleType === s ? 600 : 400,
                  background: form.scheduleType === s ? '#2563eb' : '#f3f4f6',
                  color: form.scheduleType === s ? '#fff' : '#374151',
                }}>{s}</button>
            ))}
          </div>
          {form.scheduleType === 'DAILY' && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>{t('MonitoringTab.scheduleDaily')}</div>}
          {form.scheduleType === 'HOURLY' && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>{t('MonitoringTab.scheduleHourly')}</div>}
          {form.scheduleType === 'MANUAL' && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>{t('MonitoringTab.scheduleManual')}</div>}
        </Field>

        {config?.lastAuditAt && (
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
            <i className="fas fa-clock" style={{ marginRight: '5px' }} />
            {t('MonitoringTab.lastAudit', { date: new Date(config.lastAuditAt).toLocaleString() })}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={saveConfig} disabled={saving || !dirty}
            style={{ padding: '8px 16px', background: dirty ? '#2563eb' : '#e5e7eb', color: dirty ? '#fff' : '#9ca3af', border: 'none', borderRadius: '6px', cursor: dirty ? 'pointer' : 'default', fontSize: '13px', fontWeight: 600 }}>
            {saving ? t('MonitoringTab.saving') : t('MonitoringTab.save')}
          </button>
          <button onClick={testEmail} disabled={testing || !form.recipientEmail}
            style={{ padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            <i className={`fas ${testing ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} style={{ marginRight: '5px' }} />
            {testing ? t('MonitoringTab.sending') : t('MonitoringTab.testEmail')}
          </button>
          <button onClick={runAudit} disabled={running}
            style={{ padding: '8px 14px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', cursor: running ? 'default' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
            <i className={`fas ${running ? 'fa-spinner fa-spin' : 'fa-play'}`} style={{ marginRight: '5px' }} />
            {running ? t('MonitoringTab.running') : t('MonitoringTab.runNow')}
          </button>
        </div>

        {testResult && (
          <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '6px', fontSize: '12px',
            background: testResult.error ? '#fef2f2' : '#f0fdf4',
            color: testResult.error ? '#dc2626' : '#15803d',
            border: `1px solid ${testResult.error ? '#fecaca' : '#bbf7d0'}` }}>
            {testResult.error
              ? <><i className="fas fa-times-circle" style={{ marginRight: '5px' }} />{testResult.error}</>
              : <><i className="fas fa-check-circle" style={{ marginRight: '5px' }} />{t('MonitoringTab.testEmailSent', { recipient: testResult.recipient })}</>}
          </div>
        )}

        {runResult && (
          <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '6px', fontSize: '12px',
            background: runResult.errorMessage ? '#fef2f2' : '#f0fdf4',
            color: runResult.errorMessage ? '#dc2626' : '#15803d',
            border: `1px solid ${runResult.errorMessage ? '#fecaca' : '#bbf7d0'}` }}>
            {runResult.errorMessage
              ? <><i className="fas fa-times-circle" style={{ marginRight: '5px' }} />{runResult.errorMessage}</>
              : <><i className="fas fa-check-circle" style={{ marginRight: '5px' }} />
                  {t('MonitoringTab.auditComplete', { count: runResult.newFindings })}
                  {runResult.alertSent && <span style={{ color: '#7c3aed' }}> {t('MonitoringTab.alertSentNotice')}</span>}
                </>}
          </div>
        )}
      </div>

      {/* Audit history */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
            <i className="fas fa-history" style={{ marginRight: '8px', color: '#6b7280' }} />{t('MonitoringTab.auditHistory')}
          </h3>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('MonitoringTab.runsCount', { count: totalLogs })}</span>
        </div>

        {logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', fontSize: '14px' }}>
            {t('MonitoringTab.emptyStatePrefix')} <strong>{t('MonitoringTab.runNow')}</strong> {t('MonitoringTab.emptyStateSuffix')}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[
                  { key: 'when', label: t('MonitoringTab.colWhen') },
                  { key: 'trigger', label: t('MonitoringTab.colTrigger') },
                  { key: 'new', label: t('MonitoringTab.colNew') },
                  { key: 'crit', label: 'CRIT' },
                  { key: 'high', label: 'HIGH' },
                  { key: 'med', label: 'MED' },
                  { key: 'low', label: 'LOW' },
                  { key: 'alert', label: t('MonitoringTab.colAlert') },
                ].map(h => (
                  <th key={h.key} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 12px', color: '#374151', whiteSpace: 'nowrap' }}>
                    {new Date(l.auditRunAt).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                      background: l.triggerType === 'SCHEDULED' ? '#eff6ff' : l.triggerType === 'TEST' ? '#f3f4f6' : '#ede9fe',
                      color: l.triggerType === 'SCHEDULED' ? '#1d4ed8' : l.triggerType === 'TEST' ? '#6b7280' : '#6d28d9',
                    }}>{l.triggerType}</span>
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: l.newFindings > 0 ? '#dc2626' : '#6b7280' }}>{l.newFindings}</td>
                  <td style={{ padding: '10px 12px', color: l.criticalCount > 0 ? '#dc2626' : '#9ca3af', fontWeight: l.criticalCount > 0 ? 700 : 400 }}>{l.criticalCount || '—'}</td>
                  <td style={{ padding: '10px 12px', color: l.highCount > 0 ? '#ea580c' : '#9ca3af', fontWeight: l.highCount > 0 ? 700 : 400 }}>{l.highCount || '—'}</td>
                  <td style={{ padding: '10px 12px', color: l.mediumCount > 0 ? '#d97706' : '#9ca3af', fontWeight: l.mediumCount > 0 ? 700 : 400 }}>{l.mediumCount || '—'}</td>
                  <td style={{ padding: '10px 12px', color: l.lowCount > 0 ? '#2563eb' : '#9ca3af', fontWeight: l.lowCount > 0 ? 700 : 400 }}>{l.lowCount || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {l.alertSent
                      ? <span style={{ color: '#10b981', fontSize: '12px' }}><i className="fas fa-envelope-check" style={{ marginRight: '3px' }} />{t('MonitoringTab.sent')}</span>
                      : <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <div onClick={() => onChange(!checked)}
      style={{ width: '36px', height: '20px', borderRadius: '10px', background: checked ? '#2563eb' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
      <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: checked ? '18px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
